import { Body, Box, Button, Stack } from "@biom3/react";
import type { WishlistProps } from "@ui-kit";
import { PRIZE_GIVE_AWAY_MODAL_IMAGE } from "@ui-kit/constants";
import { motion } from "motion/react";
import Image from "next/image";
import Link from "next/link";
import { useCallback } from "react";

type PrizeGiveAwayWishlistProps = {
  isWishlisted: boolean;
  wishlistProps: WishlistProps;
};

export const PrizeGiveAwayWishlist = ({
  isWishlisted,
  wishlistProps,
}: PrizeGiveAwayWishlistProps) => {
  const formatDate = useCallback((date: Date) => {
    return date.toLocaleDateString("en-AU", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  }, []);

  const getButtonTitleText = () => {
    if (wishlistProps.gemsState === "creatingWallet") {
      return "Creating your wallet";
    }
    if (isWishlisted) {
      return "You are in the draw";
    }
    return "Sign up to win a Steam Deck";
  };

  // end date is the 5th of the current month if it's before or on the 5th at 3pm AEDT (4am UTC)
  // otherwise it's the 5th of the next month
  const now = new Date();
  const currentYear = now.getUTCFullYear();
  const currentMonth = now.getUTCMonth();

  // Create date for 5th of current month at 3pm AEDT (4am UTC)
  // AEDT is UTC+11, so 3pm AEDT = 4am UTC
  const fifthAt3pmAEDTCurrentMonth = new Date();
  fifthAt3pmAEDTCurrentMonth.setUTCFullYear(currentYear, currentMonth, 5);
  fifthAt3pmAEDTCurrentMonth.setUTCHours(4, 0, 0, 0);

  const endDate =
    now <= fifthAt3pmAEDTCurrentMonth
      ? new Date(currentYear, currentMonth, 5) // 5th of current month
      : new Date(currentYear, currentMonth + 1, 5); // 5th of next month

  return (
    <Stack
      gap="base.spacing.x4"
      sx={{
        maxWidth: "800px",
        pb: "base.spacing.x12",
        pt: "base.spacing.x14",
      }}
    >
      <Stack gap="base.spacing.x4" direction="column" alignItems="center">
        <Stack gap="base.spacing.x2" alignItems="center">
          <Button
            size="large"
            variant="primary"
            rc={
              <motion.button
                onClick={() => {
                  wishlistProps.onWishlistClick("PrizeGiveAwayWishlist", {
                    prize: `steam-deck-end-date-${endDate.toISOString()}`,
                  });
                }}
                disabled={
                  wishlistProps.gemsState === "creatingWallet" ||
                  wishlistProps.gemsState === "pending"
                }
                animate={{
                  boxShadow: [
                    "0 0 10px rgba(255, 255, 255, 0.4), 0 0 20px rgba(255, 255, 255, 0.3), 0 0 30px rgba(255, 255, 255, 0.2)",
                    "0 0 20px rgba(255, 255, 255, 0.6), 0 0 30px rgba(255, 255, 255, 0.5), 0 0 40px rgba(255, 255, 255, 0.4)",
                    "0 0 10px rgba(255, 255, 255, 0.4), 0 0 20px rgba(255, 255, 255, 0.3), 0 0 30px rgba(255, 255, 255, 0.2)",
                  ],
                }}
                transition={{
                  duration: 2,
                  repeat: Number.POSITIVE_INFINITY,
                  ease: "easeInOut",
                }}
              />
            }
            sx={{
              zIndex: 5,
              width: ["100%", "100%", "auto"],
              transform: "scale(1)",
              transition: "transform 0.2s ease-in-out",
              "&:hover": {
                transform: "scale(1.05)",
              },
              ...(isWishlisted
                ? {
                    bg: "base.color.status.success.bright",
                    "&:hover:not([disabled])": {
                      boxShadow: ({ base }) =>
                        `inset 0 0 0 ${base.border.size[300]} ${base.color.status.success.bright}`,
                    },
                    "&::before": {
                      bg: "base.color.status.success.bright",
                    },
                  }
                : {}),
              justifyContent: "stretch",
              ".Button__innerSpan": { w: "100%" },
              ".Button__innerSpan__innerContent": { w: "100%" },
            }}
          >
            {wishlistProps.gemsState === "creatingWallet" ? (
              <Button.Icon icon={"Loading"} />
            ) : (
              <Button.Icon
                icon={isWishlisted ? "Tick" : "Add"}
                iconVariant="bold"
              />
            )}

            <Stack gap="0px">
              <Stack gap="0px">
                <Body sx={{ c: "inherit" }} weight="bold">
                  {getButtonTitleText()}
                </Body>
                {wishlistProps.gemsState !== "creatingWallet" &&
                  !isWishlisted && (
                    <Body
                      sx={{ c: "base.color.text.body.inverse.primary" }}
                      size="xxSmall"
                    >
                      Prize model is 512GB OLED
                    </Body>
                  )}
              </Stack>
              {wishlistProps.gemsState === "creatingWallet" && (
                <Body
                  size="xSmall"
                  sx={{
                    c: "inherit",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  Hang tight, it may take a minute
                </Body>
              )}
            </Stack>
          </Button>

          <Body size="xxSmall" sx={{ color: "base.color.text.body.secondary" }}>
            Drawn {formatDate(endDate)} 3pm AEDT.{" "}
            <Link
              href="https://www.immutable.com/terms/promotion-terms"
              target="_blank"
            >
              See Terms
            </Link>
            .
          </Body>
        </Stack>
        <Box
          sx={{
            pos: "relative",
            overflow: "hidden",
            mt: "-9rem",
            w: "100%",
            d: "flex",
            justifyContent: "center",
            "&::after": {
              content: '""',
              pos: "absolute",
              bottom: "0px",
              left: "0px",
              w: "100%",
              h: "40px",
              bg: "linear-gradient(to bottom, transparent, #1B1B1B)",
              pointerEvents: "none",
              zIndex: 1,
            },
            "&::before": {
              content: '""',
              pos: "absolute",
              top: "0px",
              right: "0px",
              h: "100%",
              bg: "linear-gradient(to left, #1B1B1B, transparent)",
              pointerEvents: "none",
              zIndex: 1,
            },
          }}
        >
          <Image
            src={PRIZE_GIVE_AWAY_MODAL_IMAGE}
            alt="Steam Deck"
            width={230}
            height={100}
          />
        </Box>
      </Stack>
    </Stack>
  );
};

export default PrizeGiveAwayWishlist;
